import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import IOrder from "../../../models/IOrder";
import IRoute from "../../../models/IRoute";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import OwButton from "../../shared/OwButton/OwButton";
import { useFormik } from "formik";
import moment from "moment";
import IProcessDailyRouteDTO from "../../../models/DTO/IProcessDailyRouteDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessDailyRouteDTO from "../../../models/DTO/IOnProcessDailyRouteDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import "./CreateDailyRouteFromOrdersSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import DailyRouteService from "../../../services/DailyRouteService";
import RouteService from "../../../services/RouteService";
import { DailyRouteStatusWarningModal } from "../../routes/DailyRouteStatusWarningModal/DailyRouteStatusWarningModal";

type Props = {
    orders: { guid: string; name: string; order: IOrder }[];
    cancelFn: (refresh?: boolean) => void;
};

interface IOrderRouteGroup {
    route: IRoute | null;
    orders: IOrder[];
}

// DraggableOrderItem Component
const DraggableOrderItem = ({ order, groupIndex }: { order: IOrder; groupIndex: number }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        return draggable({
            element: el,
            getInitialData: () => ({ orderId: order.id, sourceGroupIndex: groupIndex }),
            onDragStart: () => setIsDragging(true),
            onDrop: () => setIsDragging(false),
        });
    }, [order.id, groupIndex]);

    return (
        <div ref={ref} className={`draggable-order ${isDragging ? "dragging" : ""}`}>
            <i className="fas fa-grip-vertical me-2 text-muted" />
            <span className="flex-grow-1">{order.name}</span>
        </div>
    );
};

// DroppableRouteSection Component
const DroppableRouteSection = ({
    group,
    groupIndex,
    onDrop,
    onRemove,
}: {
    group: IOrderRouteGroup;
    groupIndex: number;
    onDrop: (orderId: number, sourceIndex: number, targetIndex: number) => void;
    onRemove: (groupIndex: number) => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isDraggedOver, setIsDraggedOver] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        return dropTargetForElements({
            element: el,
            getData: () => ({ targetGroupIndex: groupIndex }),
            onDragEnter: () => setIsDraggedOver(true),
            onDragLeave: () => setIsDraggedOver(false),
            onDrop: ({ source }) => {
                setIsDraggedOver(false);
                const { orderId, sourceGroupIndex } = source.data as { orderId: number; sourceGroupIndex: number };
                if (sourceGroupIndex !== groupIndex) {
                    onDrop(orderId, sourceGroupIndex, groupIndex);
                }
            },
        });
    }, [groupIndex, onDrop]);

    const title = group.route?.name || "unassigned";
    const canRemove = group.route !== null && group.orders.length === 0;

    return (
        <div ref={ref} className={`route-section ${isDraggedOver ? "drag-over" : ""}`}>
            <div className="d-flex align-items-center justify-content-between mb-1">
                <OwLabel title={title} isBold={true} skipTranslate={!!group.route} />
                {canRemove && (
                    <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => onRemove(groupIndex)}>
                        <i className="fas fa-times" />
                    </button>
                )}
            </div>
            {group.orders.map((order) => (
                <DraggableOrderItem key={order.guid} order={order} groupIndex={groupIndex} />
            ))}
            {group.orders.length === 0 && group.route === null && <div className="text-muted small p-2 text-center">Drop orders here</div>}
            {group.orders.length === 0 && group.route !== null && (
                <div className="text-warning small p-2 text-center">
                    <i className="fas fa-exclamation-triangle me-1" />
                    No orders assigned - remove or add orders
                </div>
            )}
        </div>
    );
};

export const CreateDailyRouteFromOrdersSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const buildRouteGroups = (): { groups: IOrderRouteGroup[]; blockedCount: number } => {
        // Filter out blocked orders (preliminary, shipped, delivered, picked up)
        const allOrders = props.orders.map((od) => od.order);
        const { validOrders, blockedOrders } = DailyRouteService.filterBlockedOrders(allOrders);

        // Filter props.orders to only include valid orders
        const validOrderDetails = props.orders.filter((od) => validOrders.some((vo) => vo.id === od.order.id));

        const unassigned: IOrder[] = [];
        const routeMap = new Map<number, { route: IRoute; orders: IOrder[] }>();

        for (const orderDetail of validOrderDetails) {
            const order = orderDetail.order;
            if (!order.route_id || !order.Route) {
                unassigned.push(order);
            } else {
                const existing = routeMap.get(order.route_id);
                if (existing) {
                    existing.orders.push(order);
                } else {
                    routeMap.set(order.route_id, { route: order.Route, orders: [order] });
                }
            }
        }

        return {
            groups: [{ route: null, orders: unassigned }, ...Array.from(routeMap.values())],
            blockedCount: blockedOrders.length,
        };
    };

    const [routeGroupsData] = useState(() => buildRouteGroups());
    const [isDisabled, setIsDisabled] = useState(true);
    const [routeGroups, setRouteGroups] = useState<IOrderRouteGroup[]>(routeGroupsData.groups);
    const [availableRoutes, setAvailableRoutes] = useState<IRoute[]>([]);
    const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [showStatusWarningModal, setShowStatusWarningModal] = useState(false);
    const [ordersNeedingStatusChange, setOrdersNeedingStatusChange] = useState<IOrder[]>([]);
    const [pendingFormValues, setPendingFormValues] = useState<{ route_date: string } | null>(null);

    // Show notification once on mount if orders were blocked
    useEffect(() => {
        if (routeGroupsData.blockedCount > 0) {
            const notification: INotification = {
                message: `${routeGroupsData.blockedCount} order(s) excluded due to invalid status (preliminary, shipped, delivered, or picked up)`,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get routes that aren't already in use
    const unusedRoutes = availableRoutes.filter((route) => !routeGroups.some((g) => g.route?.id === route.id));

    const addRouteGroup = (route: IRoute) => {
        if (routeGroups.some((g) => g.route?.id === route.id)) return;
        setRouteGroups((prev) => [...prev, { route, orders: [] }]);
    };

    const removeRouteGroup = (groupIndex: number) => {
        const group = routeGroups[groupIndex];
        if (group.route === null || group.orders.length > 0) return;
        setRouteGroups((prev) => prev.filter((_, i) => i !== groupIndex));
    };

    const moveOrder = useCallback((orderId: number, sourceGroupIndex: number, targetGroupIndex: number) => {
        setRouteGroups((prev) => {
            const newGroups = prev.map((g) => ({ ...g, orders: [...g.orders] }));

            const sourceGroup = newGroups[sourceGroupIndex];
            const orderIndex = sourceGroup.orders.findIndex((o) => o.id === orderId);
            if (orderIndex === -1) return prev;

            const [movedOrder] = sourceGroup.orders.splice(orderIndex, 1);
            newGroups[targetGroupIndex].orders.push(movedOrder);

            return newGroups;
        });
    }, []);

    // Get route groups that have a route assigned but no orders (empty groups)
    const getEmptyRouteGroups = useCallback((): IOrderRouteGroup[] => {
        return routeGroups.filter((g) => g.route !== null && g.orders.length === 0);
    }, [routeGroups]);

    const cancel = () => {
        props.cancelFn();
    };

    const getRoutes = async () => {
        try {
            const routes = await RouteService.getRoutes(userModel, supplierModel);
            setAvailableRoutes(routes);
        } catch (error) {
            console.error("Failed to fetch routes:", error);
        } finally {
            setIsLoadingRoutes(false);
        }
    };

    const processDailyRoute = (values: { route_date: string }) => {
        setIsActive(true);

        const dailyRoutes = routeGroups
            .filter((group) => group.route !== null && group.orders.length > 0)
            .map((group) => ({
                dailyRoute: {
                    route_date: values.route_date,
                    route_id: group.route!.id,
                },
                orders: group.orders.map((order) => order.id),
            }));

        if (dailyRoutes.length === 0) {
            const notification: INotification = {
                message: "no_orders_assigned_to_routes",
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        const payload: IProcessDailyRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            dailyRoutes,
        };

        socket.emit(EndpointsEnum.RoutesProcessDailyRoute, payload);
    };

    const onProcessDailyRoute = (_data: IOnProcessDailyRouteDTO) => {
        const notification: INotification = {
            message: "successfully_processed_new_daily_routes",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);

        setIsActive(false);
    };

    const onNotify = () => {
        setIsActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.RoutesOnProcessDailyRoute, fn: onProcessDailyRoute },
        { endpoint: EndpointsEnum.Notify, fn: onNotify },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getRoutes();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = (values: { route_date: string }) => {
        // Check for empty route groups (route assigned but no orders)
        const emptyGroups = getEmptyRouteGroups();
        if (emptyGroups.length > 0) {
            const routeNames = emptyGroups.map((g) => g.route!.name).join(", ");
            const notification: INotification = {
                message: `The following route groups have no orders assigned: ${routeNames}. Please remove empty groups or assign orders to them.`,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
                skipMessageTranslate: true,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        // Get all orders that will be submitted
        const ordersToSubmit = routeGroups
            .filter((group) => group.route !== null && group.orders.length > 0)
            .flatMap((group) => group.orders);

        // Check for orders needing status change (not in READY_FOR_DELIVERY or READY_FOR_PICKUP)
        const needsStatusChange = DailyRouteService.getOrdersNeedingStatusChange(ordersToSubmit);

        if (needsStatusChange.length > 0) {
            setOrdersNeedingStatusChange(needsStatusChange);
            setPendingFormValues(values);
            setShowStatusWarningModal(true);
        } else {
            processDailyRoute(values);
        }
    };

    const confirmProceed = () => {
        setShowStatusWarningModal(false);
        if (pendingFormValues) {
            processDailyRoute(pendingFormValues);
        }
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const cancelStatusWarning = () => {
        setShowStatusWarningModal(false);
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const formik = useFormik({
        initialValues: {
            route_date: moment().format("YYYY-MM-DD"),
        },
        onSubmit: (values) => {
            handleSubmit(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.route_date) {
                errors.route_date = "Required";
            }

            return errors;
        },
    });

    useEffect(() => {
        const hasValidDate = !!formik.values.route_date;
        const hasUnassignedOrders = routeGroups[0]?.orders.length > 0;
        const hasAssignedOrders = routeGroups.some((g) => g.route !== null && g.orders.length > 0);
        setIsDisabled(!hasValidDate || hasUnassignedOrders || !hasAssignedOrders);
    }, [formik.values.route_date, routeGroups]);

    return (
        <div className={"CreateDailyRouteFromOrdersSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-truck-front"} title={"create_daily_route"} />
                    <hr />
                </div>
            </div>

            <div className="row">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-3">
                        <div>
                            <OwLabel title={"route_date"} isBold={true} />
                        </div>
                        <div>
                            <OwDateInput
                                selectedDate={formik.values.route_date}
                                fn={(date: Moment) => {
                                    const e = {
                                        target: {
                                            value: date.format("YYYY-MM-DD"),
                                            name: "route_date",
                                        },
                                    };

                                    formik.handleChange(e);
                                }}
                            />
                        </div>
                    </div>

                    {/* Add Route Group Dropdown */}
                    {!isLoadingRoutes && unusedRoutes.length > 0 && (
                        <div className="mb-3">
                            <OwLabel title={"add_route_group"} isBold={true} />
                            <select
                                className="form-select form-select-sm"
                                onChange={(e) => {
                                    const routeId = parseInt(e.target.value);
                                    const route = availableRoutes.find((r) => r.id === routeId);
                                    if (route) addRouteGroup(route);
                                    e.target.value = "";
                                }}
                                defaultValue="">
                                <option value="" disabled>
                                    Select route...
                                </option>
                                {unusedRoutes.map((route) => (
                                    <option key={route.id} value={route.id}>
                                        {route.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Route Sections with Drag and Drop */}
                    <div className={"d-flex flex-column mb-4"}>
                        {isLoadingRoutes ? (
                            <div className="text-center p-3">
                                <i className="fas fa-spinner fa-spin" />
                            </div>
                        ) : (
                            routeGroups.map((group, index) => (
                                <DroppableRouteSection
                                    key={group.route?.id ?? "unassigned"}
                                    group={group}
                                    groupIndex={index}
                                    onDrop={moveOrder}
                                    onRemove={removeRouteGroup}
                                />
                            ))
                        )}
                    </div>

                    <div>
                        <OwButton type={"submit"} title={"save"} disableOnActive={true} disabled={isDisabled} active={isActive} />
                    </div>
                </form>
            </div>

            {/* Status Warning Modal */}
            <Modal
                isOpen={showStatusWarningModal}
                contentLabel="Order Status Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass">
                <DailyRouteStatusWarningModal
                    ordersNeedingStatusChange={ordersNeedingStatusChange}
                    proceedFn={confirmProceed}
                    cancelFn={cancelStatusWarning}
                />
            </Modal>
        </div>
    );
};
